import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Center, Divider, Stack, Text } from '@mantine/core';
import { useNavigate } from '@tanstack/react-router';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { PublicationFoundModal } from '@/module/Author/Publication/PublicationFoundModal';
import {
  useReclaimAuthorship,
  useSearchPublicationByIdentifiers,
} from '@/module/Author/Publication/model/PublicationQuery';
import { PublicationSearchRequest, ReclaimRequest } from '@/module/Author/Publication/model/PublicationTypes';

import { AddButtonLink, Content, TextInputControl } from '@/core/components';

import { useAppNotification } from '@feature/utils';

const schema = z.object({
  doi: z.string(),
  scopusId: z.string(),
  wosId: z.string(),
});

const defaultValues = {
  doi: '',
  scopusId: '',
  wosId: '',
};

export function PublicationSearchClaimable() {
  const form = useForm<PublicationSearchRequest>({
    defaultValues,
    resolver: zodResolver(schema),
  });
  const { t } = useTranslation();

  const { mutate, data, isPending, reset } = useSearchPublicationByIdentifiers();

  const notification = useAppNotification();
  const onSubmit = (values: PublicationSearchRequest) => {
    mutate(values, {
      onError: notification.error,
      onSuccess: notification.success,
    });
  };

  const reclaimMutation = useReclaimAuthorship();
  const isSubmitting = isPending || reclaimMutation.isPending;

  const navigate = useNavigate();
  const onReclaimAuthorship = (request: ReclaimRequest) => {
    reset();
    reclaimMutation.mutate(request, {
      onError: notification.error,
      onSuccess: () => {
        notification.success();
        navigate({ to: `../${request.publicationId}` });
      },
    });
  };

  return (
    <Content type="full">
      <Text p="md">{t('publications.claimableSearchInfo')}</Text>
      {data && (
        <PublicationFoundModal
          close={reset}
          data={data}
          onReclaimAuthorship={onReclaimAuthorship}
        />
      )}
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Stack gap="xs">
            <TextInputControl<PublicationSearchRequest>
              name="wosId"
              leftSectionWidth="60px"
              leftSection={<Text fw="500">WOS:</Text>}
              label={t('publications.form.fields.identifiers.wosId')}
              isSubmitting={isSubmitting}
            />
            <Text
              ta="center"
              fw="500"
            >
              sau
            </Text>
            <TextInputControl<PublicationSearchRequest>
              name="doi"
              leftSectionWidth="160px"
              leftSection={<Text>https://dx.doi.org/</Text>}
              label={t('publications.form.fields.identifiers.doi')}
              isSubmitting={isSubmitting}
            />
            <Text
              ta="center"
              fw="500"
            >
              sau
            </Text>
            <TextInputControl<PublicationSearchRequest>
              name="scopusId"
              leftSectionWidth="80px"
              leftSection={<Text fw="500">SCOPUS:</Text>}
              label={t('publications.form.fields.identifiers.scopusId')}
              isSubmitting={isSubmitting}
            />
            <Center>
              <Button
                loading={isSubmitting}
                type="submit"
              >
                {t('button.search')}
              </Button>
            </Center>
          </Stack>
        </form>
      </FormProvider>
      <Divider />
      <Text
        c="dimmed"
        fs="italic"
        mt="10px"
      >
        {t('publications.addNewDisclaimer')}
      </Text>
      {!isSubmitting && (
        <AddButtonLink
          to="../create"
          name={t('button.addNew')}
        />
      )}
    </Content>
  );
}
